import { Euler, MathUtils, Matrix4, Quaternion, Vector3 } from 'three';
import type { Sphere } from 'three';
import type { Fighter } from '../../rules/fighter';
import type { PunchAction, PunchStats } from '../../rules/types';
import { balls, boxerBallCount, boxerFistInGlove, gloveRadius, placeBoxerBalls } from '../bodies';
import { deepest, pushOff, pushOut, sweep } from '../collide';
import type { PunchingArm } from '../collide';
import { Landing } from '../landing';
import { reach } from './ik';
import type { ArmRig, BoxerRig } from './boxer';
import { approach, punchLoad, punchReach } from './motion';
import { Signal } from '../../rules/signal';
import { Spring } from './spring';

const target = new Vector3();
const pole = new Vector3();
const local = new Vector3();
const path = new Vector3();
const shoulderAt = new Vector3();
const along = new Vector3();
const wristAt = new Vector3();
const forward = new Vector3();
const aimed = new Quaternion();
const fist = new Vector3();
const fistBefore = new Vector3();
const meets = new Vector3();
const stanceTurn = new Quaternion();

/** How he stands between moves: his hips a little low and turned, his chest leaning in. */
const stance = { hipsHeight: 0.88, hipsTurn: 0.18, chestLean: 0.12 };

/**
 * Where each glove sits in his guard, and covering up, in his own space as he stands (he faces +Z, his left is +X). The
 * guard rides on his chest: it goes where his chest goes, as he steps into a punch, twists or weaves.
 */
const guard = { left: new Vector3(0.24, 1.4, 0.34), right: new Vector3(-0.35, 1.4, 0.21) };
const cover = { left: new Vector3(0.097, 1.7, 0.39), right: new Vector3(-0.155, 1.74, 0.383) };
/**
 * Where each punch cocks the glove as it loads, from his guard, in his space with x out to the glove's own side: a
 * straight one a little, a hook wide, one to the body out and down, an uppercut low. The glove swings in from there.
 */
const cocked: Record<PunchAction, Vector3> = {
  straight: new Vector3(0.105, -0.05, 0),
  hook: new Vector3(0.28, 0.03, -0.08),
  bodyBlow: new Vector3(0.22, -0.2, -0.05),
  uppercut: new Vector3(0.06, -0.28, -0.04),
  super: new Vector3(0.105, -0.05, 0),
};
/** How far from the player's eyes his knuckles stop: close when the face is open, on the gloves when covered. */
const knucklesStop = { open: 0.3, covered: 0.55 };
/** The furthest his body steps towards the player to land a punch. */
const longestStep = 0.45;

export interface PlayerTargets {
  /** The player's eyes, in the world. */
  readonly head: Vector3;
  /** The player's middle, in the world. */
  readonly body: Vector3;
  /** How far the player's gloves are up in front of his face: 0 in his guard, 1 covering up. */
  readonly covered: number;
  /** The player's head as balls in the world: his gloves stop on it, never pass through. */
  readonly parts: readonly Sphere[];
  /**
   * The player's gloves as balls in the world. Covered up, the player stops his punches on them; open, the player's
   * guard gives way to them, as the rules let them through.
   */
  readonly gloves: readonly Sphere[];
  /** The player's arm on its way in a punch, if any: his guard gives way to it unless he covers up. */
  readonly punching: readonly PunchingArm[];
}

type Mood = 'calm' | 'angry' | 'hurt' | 'dazed' | 'happy';

/**
 * Moves the boxer from what the rules say he is doing: his living guard, each punch along its path exactly in time
 * with its phases, his cover, the jolts of being hit, a stagger, the fall of a knockout away from the player, the joy
 * of a win. His eyes follow the player; his brows and mouth show how he feels.
 */
export class BoxerAnimator {
  private time = 0;
  private blinkIn = 2;
  private blinking = 0;
  private coverAmount = 0;
  private readonly headPitch = new Spring(0, 220, 14);
  private readonly headYaw = new Spring(0, 220, 14);
  private readonly lean = new Spring(0, 140, 12);
  private readonly crunch = new Spring(0, 160, 12);
  /** Knocked back by a punch, or stumbling from a big one: along the line to the player, springing back. */
  private readonly shove = new Spring(0, 90, 10);
  /** Where he stands when he is not moving about: set by the game before the first frame. */
  private readonly home = new Vector3();
  private homed = false;
  /** Footwork: how far he has circled to the side, where he is heading, and when he picks a new spot. */
  private sidestep = 0;
  private sidestepTo = 0;
  private nextStep = 1.5;
  private stepPhase = 0;
  private wasStaggered = false;
  /** How worn out he looks: it follows his stamina over a moment, as a punch's cost lands all at once. */
  private tiredness = 0;
  /** How far through a bounce on his feet: it runs slower when he is tired, never jumping. */
  private bouncePhase = 0;
  /** How much of his weave and of his stagger's wobble show: each comes and goes over a moment, never in a frame. */
  private weaving = 0;
  private wobbling = 0;
  /** What moves his hips and chest this frame, besides the punch. */
  private readonly body = { t: 0, tired: 0, idle: 1, wobble: 0, bounce: 0, weave: 0, lean: 0, crunch: 0 };
  private readonly gloves = { left: new Vector3(), right: new Vector3() };
  private placed = false;
  private fall = -1;
  private floorHit = false;
  /** He hit the canvas at the end of a knockout fall, here. */
  readonly floored = new Signal<Vector3>();
  private win = -1;
  private mood: Mood = 'calm';
  private moodLeft = 0;
  /** How far his body must step in for the punch he is throwing to land with a nearly straight arm. */
  private step = 0;
  /** His own head, jaw, chest and belly, placed each frame before his arms. */
  private readonly ownParts = balls(boxerBallCount);
  /** What his guard, and his punches, rest on; and of those, what is the player's. */
  private readonly guardParts: Sphere[] = [];
  private readonly punchParts: Sphere[] = [];
  private readonly playerParts: Sphere[] = [];
  /** Where each wrist last was with its glove touching nothing. */
  private readonly clear = { left: new Vector3(), right: new Vector3() };
  /** Where each fist was placed last frame, and this frame: a punch travels from one to the next. */
  private readonly fistsBefore = { left: new Vector3(), right: new Vector3() };
  private readonly fists = { left: new Vector3(), right: new Vector3() };
  /** Where each hand's punch really got to. */
  private readonly landings = { left: new Landing(), right: new Landing() };
  /** Where each wrist is placed, its glove stopped on what it met; the next frame starts again from where it heads. */
  private readonly wrists = { left: new Vector3(), right: new Vector3() };
  /** From his chest back to his own space as he stands: the guard's frame is his chest's now, times this. */
  private readonly chestToStance = new Matrix4();
  private readonly guardFrame = new Matrix4();
  /** How each arm was last placed: settle places it again the same way. */
  private readonly fitted: Record<'left' | 'right', { side: 1 | -1; punch: PunchStats | undefined; extension: number; player: PlayerTargets } | null> = { left: null, right: null };

  constructor(private readonly rig: BoxerRig) {
    const hips = new Matrix4().compose(new Vector3(0, stance.hipsHeight, 0), stanceTurn.setFromEuler(new Euler(0, stance.hipsTurn, 0)), new Vector3(1, 1, 1));
    const chest = new Matrix4().compose(rig.chest.position, stanceTurn.setFromEuler(new Euler(stance.chestLean, 0, 0)), new Vector3(1, 1, 1));
    this.chestToStance.multiplyMatrices(hips, chest).invert();
  }

  /** True while his gloves are up covering his face. */
  get covered(): boolean {
    return this.coverAmount > 0.5;
  }

  /** A clean hit: his head snaps away from the punch, or he doubles over from a body blow. */
  hit(punch: PunchStats, strength: number): void {
    this.shove.kick(-1.1 * strength);
    if (punch.target === 'body') {
      this.crunch.kick(4 * strength);
    } else {
      this.headPitch.kick(-7 * strength * (punch.action === 'uppercut' ? 1.6 : 1));
      const sideways = punch.action === 'hook' ? (punch.hand === 'left' ? -1 : 1) : punch.hand === 'left' ? -0.4 : 0.4;
      this.headYaw.kick(sideways * 9 * strength);
      this.lean.kick(-2.5 * strength);
    }
    this.feel('hurt', 0.6);
  }

  /** A punch into his guard: the gloves give a little. */
  blocked(): void {
    this.lean.kick(-1);
  }

  knockedOut(): void {
    this.fall = 0;
    this.floorHit = false;
    this.feel('dazed', 99);
  }

  celebrate(): void {
    this.win = 0;
    this.feel('happy', 99);
  }

  /** Back in his corner for a new bout. */
  reset(): void {
    this.fall = this.win = -1;
    this.sidestep = this.sidestepTo = 0;
    if (this.homed) this.rig.root.position.copy(this.home);
    this.placed = false;
    this.mood = 'calm';
    this.moodLeft = 0;
  }

  update(seconds: number, me: Fighter, player: PlayerTargets): void {
    this.time += seconds;
    const t = this.time;
    const rig = this.rig;
    if (this.moodLeft > 0 && (this.moodLeft -= seconds) <= 0) this.mood = 'calm';
    if (me.state === 'staggered') this.feel('dazed', 0.3);
    if (me.state === 'punching') this.feel('angry', 0.25);

    const punch = me.currentPunch;
    const extension = punch ? punchReach(punch) : 0;
    const load = punch ? punchLoad(punch) : 0;
    const stats = punch?.stats;
    if (punch) this.landings[punch.stats.hand].follow(punch);
    this.tiredness = approach(this.tiredness, 1 - me.staminaFraction, 4, seconds);
    const tired = this.tiredness;

    this.footwork(seconds, me, player);

    const body = this.body;
    body.t = t;
    body.tired = tired;
    // Out cold, nothing of him moves but the fall.
    body.idle = this.fall >= 0 ? Math.max(0, 1 - this.fall / 0.6) : 1;
    // Bounce on the balls of his feet, slower when he is tired; a wobble when staggered.
    this.wobbling = approach(this.wobbling, me.state === 'staggered' ? 1 : 0, 10, seconds);
    body.wobble = Math.sin(t * 9) * 0.12 * this.wobbling;
    this.bouncePhase += seconds * (3.4 - tired * 1.2);
    body.bounce = Math.abs(Math.sin(this.bouncePhase)) * 0.025 * body.idle;
    // A slow weave of the shoulders in his guard, so he is never a statue.
    this.weaving = approach(this.weaving, me.state === 'ready' ? 1 : 0, 5, seconds);
    body.weave = (Math.sin(t * 1.7) * 0.07 + Math.sin(t * 0.63) * 0.04) * this.weaving * body.idle;
    body.lean = this.lean.update(0, seconds);
    body.crunch = this.crunch.update(0, seconds);
    // He steps into a punch just far enough to reach, and rocks back a little while he loads it. How far is measured
    // with his body as it will be when the punch lands, twisted into it, so the step holds still all through the punch.
    if (stats) {
      this.pose(1, stats, 0);
      this.step = this.stepFor(stats, player);
    }
    this.pose(extension, stats, extension * this.step);
    // Legs: his stance, and each foot lifting in turn as he steps about.
    const lift = (phase: number) => Math.max(0, Math.sin(this.stepPhase + phase));
    rig.legs[0].thigh.rotation.set(Math.PI / 2 - 0.25 - lift(0) * 0.35, 0, -0.08);
    rig.legs[1].thigh.rotation.set(Math.PI / 2 + 0.2 - lift(Math.PI) * 0.35, 0, 0.08);
    rig.legs[0].shin.rotation.x = 0.35 + body.bounce * 2 + lift(0) * 0.6;
    rig.legs[1].shin.rotation.x = 0.35 + body.bounce * 2 + lift(Math.PI) * 0.6;

    this.coverAmount = approach(this.coverAmount, me.state === 'blocking' ? 1 : 0, 22, seconds);
    this.fistsBefore.left.copy(this.fists.left);
    this.fistsBefore.right.copy(this.fists.right);
    rig.root.updateMatrixWorld(true);
    this.guardFrame.multiplyMatrices(rig.chest.matrixWorld, this.chestToStance);
    placeBoxerBalls(rig, this.ownParts);
    // Whoever covers up stops the other's gloves on his own; whoever does not makes way for them.
    this.guardParts.length = this.punchParts.length = this.playerParts.length = 0;
    this.guardParts.push(...this.ownParts, ...player.parts);
    this.playerParts.push(...player.parts, ...(player.covered > 0.5 ? player.gloves : []));
    this.punchParts.push(...this.ownParts, ...this.playerParts);
    this.arm(rig.left, 1, stats?.hand === 'left' ? stats : undefined, extension, load, player, seconds, t);
    this.arm(rig.right, -1, stats?.hand === 'right' ? stats : undefined, extension, load, player, seconds, t + 0.7);
    this.placed = true;

    this.fallAndWin(seconds);
    this.look(player.head, seconds);
    this.face(seconds);
    // The skinned body follows the joints, now that they are all in place.
    rig.root.updateMatrixWorld(true);
    rig.body.update();
  }

  // His hips and chest with the punch this far along, stepped in so far.
  private pose(extension: number, stats: PunchStats | undefined, step: number): void {
    const { t, tired, idle, wobble, bounce, weave, lean, crunch } = this.body;
    const twist = stats ? (stats.hand === 'left' ? -1 : 1) * (stats.action === 'straight' ? 0.35 : 0.55) * Math.max(0, extension) : 0;
    this.rig.hips.position.set(Math.sin(t * 0.9) * 0.05 * idle + wobble * 0.3, stance.hipsHeight + bounce - tired * 0.03, step);
    this.rig.hips.rotation.set(0, stance.hipsTurn + Math.sin(t * 1.3) * 0.04 * idle + twist * 0.5, wobble * 0.3);
    this.rig.chest.rotation.set(
      stance.chestLean + tired * 0.14 + Math.sin(t * 2.1) * 0.015 * idle + Math.max(0, extension) * 0.12 + lean * 0.05 + crunch * 0.12,
      Math.sin(t * 1.1) * 0.05 * idle + twist,
      wobble * 0.5 + weave,
    );
  }

  // How far his body must step in, with it posed as the punch lands, for the punching arm to reach nearly straight.
  private stepFor(punch: PunchStats, player: PlayerTargets): number {
    this.rig.root.updateMatrixWorld(true);
    (punch.hand === 'left' ? this.rig.left : this.rig.right).shoulder.getWorldPosition(shoulderAt);
    this.stopFor(punch, player, shoulderAt, wristAt);
    const armLength = this.rig.upperArmLength + this.rig.foreArmLength;
    return MathUtils.clamp(shoulderAt.distanceTo(wristAt) - armLength * 0.97, 0, longestStep);
  }

  // The knuckles stop short of the camera, and further out on the player's gloves when he covers up: the rules decide
  // the hit, the picture only has to look like it. The wrist stops a glove's length behind them, on the line from his
  // shoulder, so a straight arm points the glove right at the player.
  private stopFor(punch: PunchStats, player: PlayerTargets, shoulder: Vector3, out: Vector3): Vector3 {
    const aim = punch.target === 'body' ? player.body : player.head;
    const stop = this.rig.gloveLength + MathUtils.lerp(knucklesStop.open, knucklesStop.covered, player.covered);
    return out.copy(aim).addScaledVector(along.subVectors(aim, shoulder).normalize(), -stop);
  }

  private arm(arm: ArmRig, side: 1 | -1, punch: PunchStats | undefined, extension: number, load: number, player: PlayerTargets, seconds: number, t: number): void {
    const key = side === 1 ? 'left' : 'right';
    const home = local.copy(guard[key]).lerp(cover[key], this.coverAmount);
    // Up into his cover and back down, the gloves pass in front of his chin, not through it.
    home.z += Math.sin(this.coverAmount * Math.PI) * 0.12;
    // Worn out, his hands sag from his face.
    home.y -= this.tiredness * 0.16;
    home.z -= this.tiredness * 0.04;
    home.x += Math.sin(t * 2.3) * 0.015;
    home.y += Math.sin(t * 3.1) * 0.02;
    target.copy(home).applyMatrix4(this.guardFrame);
    if (this.win >= 0) this.toWorld(local.set(side * 0.35, 2.15, 0.1), target);
    // Out cold: the arms fly loose and flop out to the sides.
    if (this.fall >= 0) this.toWorld(local.set(side * 0.62, 1.3, -0.05), target);

    if (punch && extension !== 0) {
      this.stopFor(punch, player, arm.shoulder.getWorldPosition(shoulderAt), wristAt);
      // Once it stopped on something, and on its way back, from where the punch really got to.
      if (this.landings[key].holds) wristAt.copy(this.landings[key].at);
      path.subVectors(wristAt, target);
      const out = Math.max(0, extension);
      const loaded = Math.max(0, -extension);
      // Loading a punch cocks the glove back and out, never into his own face; it swings in from there as it strikes.
      const cock = local.copy(cocked[punch.action]);
      cock.x *= side;
      target.addScaledVector(path, out - loaded * 0.3).add(cock.multiplyScalar(load).applyQuaternion(this.rig.root.quaternion));
      this.gloves[key].copy(target);
    } else if (!this.placed) {
      this.gloves[key].copy(target);
    } else {
      const glove = this.gloves[key];
      glove.x = approach(glove.x, target.x, 18, seconds);
      glove.y = approach(glove.y, target.y, 18, seconds);
      glove.z = approach(glove.z, target.z, 18, seconds);
    }
    this.fitted[key] = { side, punch, extension, player };
    this.fit(arm, key);
  }

  /**
   * Once the player's arms have moved this frame, his gloves are placed again against where the player's head and
   * gloves are now, so the two never overlap even for a frame.
   */
  settle(): void {
    this.fit(this.rig.left, 'left');
    this.fit(this.rig.right, 'right');
    this.rig.root.updateMatrixWorld(true);
    this.rig.body.update();
  }

  private fit(arm: ArmRig, key: 'left' | 'right'): void {
    const fitted = this.fitted[key];
    if (!fitted) return;
    const { side, punch, extension, player } = fitted;
    pole.set(side * 0.6, -1, -0.35).applyQuaternion(this.rig.root.quaternion);
    // From where the glove heads, every time: a push off what it met is never carried into the next frame, where the
    // glove would head back in and be pushed out again, and shake.
    const wrist = this.wrists[key].copy(this.gloves[key]);
    const place = () => {
      reach(arm.upper, arm.fore, this.rig.upperArmLength, this.rig.foreArmLength, wrist, pole);
      // The wrist turns the knuckles onto the target as the punch lands, however bent the arm: a hook comes in from
      // the side and still hits the face.
      arm.glove.quaternion.identity();
      if (punch && extension > 0) {
        arm.glove.lookAt(punch.target === 'body' ? player.body : player.head);
        aimed.copy(arm.glove.quaternion);
        arm.glove.quaternion.identity().slerp(aimed, extension);
      }
    };
    const punching = punch !== undefined && extension > 0;
    const parts = punching ? this.punchParts : this.guardParts;
    const armLength = this.rig.upperArmLength + this.rig.foreArmLength;
    for (let pass = 0; pass < 6; pass++) {
      place();
      arm.glove.updateWorldMatrix(true, false);
      arm.glove.localToWorld(fist.copy(boxerFistInGlove));
      arm.shoulder.getWorldPosition(shoulderAt);
      along.subVectors(wrist, shoulderAt).normalize();
      // A punch stops where it first meets the player on its way from the last frame, however far it would go in one:
      // put anywhere else, it would snap back or swing off, frame to frame.
      if (punching && pass === 0 && this.placed) {
        const share = sweep(this.fistsBefore[key], fist, gloveRadius, this.playerParts);
        if (share < 1) {
          // The fist goes where it first touched; the wrist wherever puts it there, turned onto the target as it is.
          meets.lerpVectors(this.fistsBefore[key], fist, share);
          for (let aim = 0; aim < 3; aim++) {
            wrist.add(meets).sub(fist);
            place();
            arm.glove.updateWorldMatrix(true, false);
            arm.glove.localToWorld(fist.copy(boxerFistInGlove));
          }
          this.landings[key].stop(wrist);
          continue;
        }
      }
      // Where the fist is in a head, body or glove, it slides off the surface: gloves rest on what they meet, they
      // never pass through.
      fistBefore.copy(fist);
      const pushed = pushOut(fist, gloveRadius, parts);
      const shoved = !punching && !this.covered && pushOff(fist, gloveRadius, player.punching);
      if (!pushed && !shoved) break;
      const moved = fist.sub(fistBefore);
      // An arm too straight to reach round what it met stops short, back along the arm.
      if (fistBefore.copy(wrist).add(moved).distanceTo(shoulderAt) < armLength * 0.99) wrist.add(moved);
      else wrist.addScaledVector(along, -(moved.length() + 0.005));
    }
    // Wedged where parts meet and still in after every pass: the arm stays where it last was clear.
    arm.glove.updateWorldMatrix(true, false);
    arm.glove.localToWorld(fist.copy(boxerFistInGlove));
    // Nothing to fall back on before the first clear frame (still at the origin).
    if (deepest(fist, gloveRadius, parts) > 0.005 && this.clear[key].lengthSq() > 0) {
      wrist.copy(this.clear[key]);
      place();
      arm.glove.updateWorldMatrix(true, false);
      arm.glove.localToWorld(fist.copy(boxerFistInGlove));
    } else {
      this.clear[key].copy(wrist);
    }
    this.fists[key].copy(fist);
    if (punching) this.landings[key].reach(wrist);
  }

  // He moves about the ring: a few steps to one side or the other now and then, never while punching or down; a
  // stumble back from a big punch; always turned to face the player.
  private footwork(seconds: number, me: Fighter, player: PlayerTargets): void {
    const root = this.rig.root;
    if (!this.homed) {
      this.home.copy(root.position);
      root.rotation.order = 'YXZ';
      this.homed = true;
    }
    const free = me.state === 'ready' || me.state === 'blocking';
    this.nextStep -= seconds;
    if (this.nextStep <= 0 && free) {
      this.sidestepTo = (Math.random() * 2 - 1) * 0.32;
      this.nextStep = 1.2 + Math.random() * 2.2;
    }
    const before = this.sidestep;
    if (free && this.fall < 0) this.sidestep = approach(this.sidestep, this.sidestepTo, 3, seconds);
    const moving = Math.abs(this.sidestep - before) / Math.max(seconds, 1e-6);
    this.stepPhase += seconds * Math.min(14, moving * 60);
    if (moving < 0.02) this.stepPhase = approach(this.stepPhase, Math.round(this.stepPhase / Math.PI) * Math.PI, 12, seconds);
    const staggered = me.state === 'staggered';
    if (staggered && !this.wasStaggered) this.shove.kick(-2.4);
    this.wasStaggered = staggered;
    const back = this.shove.update(0, seconds) * 0.1;
    // Along the line from the player, so a punch knocks him straight away from it.
    const toPlayer = forward.set(player.head.x - root.position.x, 0, player.head.z - root.position.z).normalize();
    root.position.set(this.home.x + this.sidestep + toPlayer.x * back, this.home.y, this.home.z + toPlayer.z * back);
    if (this.fall < 0) root.rotation.y = Math.atan2(player.head.x - root.position.x, player.head.z - root.position.z);
  }

  // Down he goes, backwards, away from the player; or up go his arms.
  private fallAndWin(seconds: number): void {
    const root = this.rig.root;
    if (this.fall >= 0) {
      this.fall += seconds;
      const drop = Math.min(1, this.fall / 0.9);
      const eased = drop * drop;
      const bounce = drop >= 1 ? Math.max(0, Math.sin((this.fall - 0.9) * 10) * 0.06 * Math.exp(-(this.fall - 0.9) * 4)) : 0;
      root.rotation.x = -eased * (Math.PI / 2 - 0.08) + bounce;
      this.rig.hips.position.y -= eased * 0.35;
      if (drop >= 1 && !this.floorHit) {
        this.floorHit = true;
        this.floored.emit(this.rig.hips.getWorldPosition(new Vector3()));
      }
    } else {
      root.rotation.x = 0;
    }
    if (this.win >= 0) {
      this.win += seconds;
      this.rig.hips.position.y += Math.abs(Math.sin(this.win * 6)) * 0.08;
    }
  }

  private toWorld(point: Vector3, out: Vector3): Vector3 {
    this.rig.root.updateWorldMatrix(true, false);
    return out.copy(point).applyMatrix4(this.rig.root.matrixWorld);
  }

  // The head turns to the player, springing away from the punches that land; out cold, it lolls back and to the side.
  private look(at: Vector3, seconds: number): void {
    const head = this.rig.head;
    if (this.fall >= 0) {
      head.rotation.set(approach(head.rotation.x, -0.45, 5, seconds), approach(head.rotation.y, 0.5, 5, seconds), approach(head.rotation.z, 0.3, 5, seconds));
      return;
    }
    head.parent!.updateWorldMatrix(true, false);
    head.rotation.set(0, 0, 0);
    head.updateWorldMatrix(false, false);
    local.copy(at);
    head.worldToLocal(local);
    const yaw = MathUtils.clamp(Math.atan2(local.x, local.z), -0.8, 0.8);
    const pitch = MathUtils.clamp(-Math.atan2(local.y - 0.15, Math.hypot(local.x, local.z)), -0.5, 0.4);
    head.rotation.set(pitch + this.headPitch.update(0, seconds) * 0.08, yaw + this.headYaw.update(0, seconds) * 0.06, this.headYaw.value * 0.03);
  }

  private feel(mood: Mood, seconds: number): void {
    if (this.mood === 'dazed' && this.moodLeft > 50) return;
    this.mood = mood;
    this.moodLeft = Math.max(this.moodLeft, seconds);
  }

  private face(seconds: number): void {
    const face = this.rig.face;
    this.blinkIn -= seconds;
    if (this.blinkIn <= 0) {
      this.blinking = 0.14;
      this.blinkIn = 2 + Math.random() * 3;
    }
    this.blinking = Math.max(0, this.blinking - seconds);
    let shut = this.blinking > 0 ? Math.sin((this.blinking / 0.14) * Math.PI) : 0;
    let browTilt = 0;
    let browLift = 0;
    let mouthOpen = 0.2;
    switch (this.mood) {
      case 'angry':
        browTilt = 0.45;
        browLift = -0.004;
        mouthOpen = 0.5;
        break;
      case 'hurt':
        browTilt = -0.4;
        browLift = 0.006;
        shut = Math.max(shut, 0.55);
        mouthOpen = 1.6;
        break;
      case 'dazed':
        browTilt = -0.3;
        shut = Math.max(shut, this.fall >= 0 ? 0.85 : 0.4 + Math.sin(this.time * 3) * 0.2);
        mouthOpen = 1.2;
        break;
      case 'happy':
        browLift = 0.01;
        mouthOpen = 1.8;
        break;
    }
    face.lids.forEach((lid) => (lid.scale.y = Math.max(0.001, shut)));
    face.brows.forEach((brow, i) => {
      const side = i === 0 ? 1 : -1;
      brow.rotation.z = MathUtils.lerp(brow.rotation.z, -side * browTilt, 0.3);
      brow.position.y = MathUtils.lerp(brow.position.y, browLift, 0.3);
    });
    face.mouth.scale.y = MathUtils.lerp(face.mouth.scale.y, mouthOpen, 0.3);
    // The eyes wander to the player's gloves and back.
    const glance = Math.sin(this.time * 0.7) * 0.2;
    face.eyes.forEach((eye) => (eye.rotation.y = glance));
  }
}
